import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { CommonButton } from '../../../../Styles/CommonStyles';
import ReturnReqFlow from './ReturnReqFlow';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { generateAssetsPDF } from '../utils/pdfGenerator';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';

import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import Chip from '@mui/material/Chip';
import UnivMyAssetComp from '../../Myasset/UnivMyAssetComp';

// Import role-specific form components
import UserReturnInboxForm from './UserReturnInboxForm';
import BoReturnInboxForm from './BoReturnInboxForm';
import StoreReturnInboxForm from './StoreReturnInboxForm';
import UnitHeadReturnInboxForm from './UnitHeadReturnInboxForm';

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const BackButton = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: #1976d2;

  &:hover {
    text-decoration: underline;
  }
`;

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ActionButton = styled(Button)`
  background-color: #1976d2;
`;

const ActionContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-self: flex-start;
`;

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

const ModalTitle = styled.h2`
  margin-bottom: 20px;
  color: #1976d2;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 10px;
`;

const SelectedAssetsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const SelectedAssetsTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SelectedAssetsTitle = styled.h3`
  margin-bottom: 0;
  color: #1976d2;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// Add new styled components for the modify selection modal
const ModifySelectionModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

// Add a new styled component for the single-line Asset Type cell
const SingleLineCell = styled(TableCell)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const UserAsstRtrnReqDetails = ({ row, setChangepage }) => {
  const [remark, setRemark] = useState('');
  const [showActionOptions, setShowActionOptions] = useState(false);
  const [userRole, setUserRole] = useState("user");
  const [assetDetails, setAssetDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [openModifyModal, setOpenModifyModal] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [hasModifiedAssets, setHasModifiedAssets] = useState(false);
  
  const editor = useRef(null);
  const auth = useAuth();
  let user_current_office = (useSelector((state) => state.office.off));

  // Note: No longer need to fetch asset details API since data is embedded

  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { officedata } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      officedata: data?.entities
    }),
  });

  // Get store offices data
  const { storeoffices } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });

  const BO_id = officedata ? Object.values(officedata)?.find(office => office?.name === user_current_office)?.Bo : "";
  const UnitHead_id = officedata ? Object.values(officedata)?.find(office => office?.name === user_current_office)?.unitHead : "";
  const StoreL1_id = storeoffices ? Object.values(storeoffices)?.find(office => office?.storeoffice === row?.req_store)?.roleUsers?.L1ReqApprover_A : "";

  // Keep the API mutation here as a reference, but the actual mutations will be in the child components

  // Check if user is a BO (Branch Officer)
  const isBO = auth?.roles?.main?.includes('Bo') ||
    Object.values(auth?.roles || {}).some(roles =>
      Array.isArray(roles) && roles.includes('Bo'));

  // Check if user is a Unit Head
  const isUnitHead = auth?.roles?.main?.includes('UnitHead') ||
    Object.values(auth?.roles || {}).some(roles =>
      Array.isArray(roles) && roles.includes('UnitHead'));

  // Check if user is a StoreL1 (Store Level 1 Approver)
  const isStoreL1 = auth?.roles?.main?.includes('L1ReqApprover_A') ||
    Object.values(auth?.roles || {}).some(roles =>
      Array.isArray(roles) && roles.includes('L1ReqApprover_A'));

  // Check if user is a StoreL2 (Store Level 2 Approver)
  const isStoreL2 = auth?.roles?.main?.includes('L2ReqApprover_A') ||
    Object.values(auth?.roles || {}).some(roles =>
      Array.isArray(roles) && roles.includes('L2ReqApprover_A'));

  // Check if this is a request sent back for modification
  const isModificationRequest = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'bo_return_to_user_mod' &&
    row?.req_data?.[row.req_data.length - 1]?.sent_to?.id === auth?.id;

  // Process asset details from the embedded data structure
  useEffect(() => {
    // Check if row and assets_requested exist
    if (!row?.assets_requested || row.assets_requested.length === 0) {
      setAssetDetails({});
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Extract asset details from the new embedded data structure
    const assetRequests = row.assets_requested;
    const detailsMap = {};
    
    // Loop through all asset request entries and collect asset details
    assetRequests?.forEach(request => {
      if (request.data && Array.isArray(request.data)) {
        // Data field now contains complete asset objects
        request.data.forEach(asset => {
          if (asset && asset.id) {
            // Map the new structure to match the expected format
            detailsMap[asset.id] = {
              _id: asset.id,
              id: asset.id,
              oem_tag: asset.oem_tag,
              org_tag: asset.org_tag || '', // fallback if not present
              name: asset.assetName,
              specification: asset.spec,
              purchase_date: asset.pur_date,
              warrantyexp_date: asset.wrty_date
            };
          }
        });
      }
    });
    
    setAssetDetails(detailsMap);
    setLoading(false);
  }, [row?.assets_requested]);

  const handleBack = () => {
    setChangepage([true, false, false, false, false]);
  };

  const handleTakeAction = () => {
    setShowActionOptions(true);
  };

  // Handle modify selection
  const handleModifySelection = () => {
    // Initialize selectedAssets with the current assets from the latest request
    const currentAssets = [];
    if (row?.assets_requested && row.assets_requested.length > 0) {
      const latestRequest = row.assets_requested[row.assets_requested.length - 1];
      if (latestRequest.data && Array.isArray(latestRequest.data)) {
        latestRequest.data?.forEach(assetObj => {
          if (assetObj && assetObj.id) {
            // Convert the embedded asset object to the format expected by the modal
            const asset = {
              _id: assetObj.id,
              id: assetObj.id,
              oem_tag: assetObj.oem_tag,
              org_tag: assetObj.org_tag || '',
              name: assetObj.assetName,
              specification: assetObj.spec,
              purchase_date: assetObj.pur_date,
              warrantyexp_date: assetObj.wrty_date
            };
            currentAssets.push(asset);
          }
        });
      }
    }
    setSelectedAssets(currentAssets);
    setOpenModifyModal(true);
  };

  // Handle closing the modify selection modal
  const handleCloseModifyModal = () => {
    setOpenModifyModal(false);
    if (!hasModifiedAssets) {
      setSelectedAssets([]);
    }
  };

  // Handle confirming the modified asset selection
  const handleConfirmModification = async () => {
    if (selectedAssets.length === 0) {
      alert("Please select at least one asset to return");
      return;
    }

    // Set flag to indicate that assets have been modified
    setHasModifiedAssets(true);
    
    // Close the modal
    setOpenModifyModal(false);
  };

  // Check user roles
  useEffect(() => {
    if (auth) {
      if (isStoreL2) {
        setUserRole("storeL2");
      } else if (isStoreL1) {
        setUserRole("storeL1");
      } else if (isUnitHead) {
        setUserRole("unitHead");
      } else if (isBO) {
        setUserRole("bo");
      } else {
        setUserRole("user");
      }
    }
  }, [auth, isBO, isUnitHead, isStoreL1, isStoreL2]);

  // Helper function to check if this request is for the current user to action
  const isRequestForUser = () => {
    const lastAction = row?.req_data?.[row.req_data.length - 1];
    if (!lastAction) return false;
    
    return lastAction.sent_to?.id === auth?.id;
  };

  // Check if the request was closed or rejected
  const isRequestClosedOrRejected = 
    row?.curr_status === 'Closed' || 
    row?.curr_status === 'Rejected' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'bo_return_req_rejected' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'store_return_rejected' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'store_return_approved';

  // Generate summary data from assets - updated to handle both original and modified assets
  const assetSummary = React.useMemo(() => {
    const summary = {};
    
    // If user has modified assets, use the selectedAssets instead of row.assets_requested
    if (hasModifiedAssets && selectedAssets.length > 0) {
      selectedAssets?.forEach(asset => {
        const name = asset.name || 'Unknown Asset';
        if (summary[name]) {
          summary[name].count += 1;
          summary[name].assets.push(asset);
        } else {
          summary[name] = {
            count: 1,
            assets: [asset]
          };
        }
      });
    } 
    // Otherwise use original assets from the most recent request object only
    else if (!hasModifiedAssets && row?.assets_requested && row.assets_requested.length > 0) {
      // Get only the latest/most recent asset request object
      const latestRequest = row.assets_requested[row.assets_requested.length - 1];
      
      if (latestRequest.data && Array.isArray(latestRequest.data)) {
        // Process each asset object from the latest request only
        latestRequest?.data?.forEach(assetObj => {
          if (assetObj && assetObj.id) {
            // Use the asset object directly from the embedded data
            const asset = {
              _id: assetObj.id,
              id: assetObj.id,
              oem_tag: assetObj.oem_tag,
              org_tag: assetObj.org_tag || '',
              name: assetObj.assetName,
              specification: assetObj.spec,
              purchase_date: assetObj.pur_date,
              warrantyexp_date: assetObj.wrty_date
            };
            
            const name = asset.name || 'Unknown Asset';
            if (summary[name]) {
              summary[name].count += 1;
              summary[name].assets.push(asset);
            } else {
              summary[name] = {
                count: 1,
                assets: [asset]
              };
            }
          }
        });
      }
    }
    
    return Object.entries(summary).map(([name, data]) => ({
      name,
      count: data.count,
      assets: data.assets
    }));
  }, [row?.assets_requested, selectedAssets, hasModifiedAssets]);

  // Get appropriate asset IDs based on row type and user
  const getAssetIds = () => {
    if (row?.req_type?.includes('Ind_Asset')) {
      return getSingleuser?.[auth?.id]?.indi_assets?.[user_current_office] || [];
    } else {
      return user_current_office && getSingleuser?.[auth?.id]?.assets?.[user_current_office] 
        ? getSingleuser[auth?.id].assets[user_current_office] 
        : [];
    }
  };
// Debug logs removed - data structure updated to use embedded asset objects
  // Let's add a helper function to get the latest sent_to_tag from the request
  const getLatestSentToTag = () => {
    if (!row?.req_data || row.req_data.length === 0) return '';
    return row.req_data[row.req_data.length - 1]?.sent_to_tag || '';
  };

  // Let's add a debug log to check the current tag
  useEffect(() => {
    if (row?.req_data?.length > 0) {
      console.log("Current request tag:", getLatestSentToTag());
    }
  }, [row]);

  // Helper function to render the appropriate form based on user role and request status
  const renderRoleSpecificForm = () => {
    // If the request is not meant for this user, don't show action options
    if (!isRequestForUser()) {
      return null;
    }

    // Check if this is a request at the Unit Head stage
    const isAtUnitHeadStage = ["bo_forwarded_to_unithead", "boreq_fwd_unithead"]?.includes(row?.curr_sent_to_tag);
    
    // If this is a unit head and the request is at unit head stage
    if (userRole === "unitHead" && isAtUnitHeadStage) {
      return (
        <UnitHeadReturnInboxForm
          row={row}
          remark={remark}
          setRemark={setRemark}
          auth={auth}
          user_current_office={user_current_office}
          getSingleuser={getSingleuser}
          handleBack={handleBack}
          BO_id={BO_id}
          StoreL1_id={StoreL1_id}
        />
      );
    }
    switch (userRole) {
      case "user":
        return (
          <UserReturnInboxForm
            row={row}
            remark={remark}
            setRemark={setRemark}
            handleModifySelection={handleModifySelection}
            isModificationRequest={isModificationRequest}
            auth={auth}
            user_current_office={user_current_office}
            getSingleuser={getSingleuser}
            handleBack={handleBack}
            BO_id={BO_id}
            selectedAssets={selectedAssets}
          />
        );
      case "bo":
        return (
          <BoReturnInboxForm
            row={row}
            remark={remark}
            setRemark={setRemark}
            auth={auth}
            user_current_office={user_current_office}
            getSingleuser={getSingleuser}
            handleBack={handleBack}
            BO_id={BO_id}
            StoreL1_id={StoreL1_id}
            UnitHead_id={UnitHead_id}
          />
        );
      case "storeL1":
      case "storeL2":
        return (
          <StoreReturnInboxForm
            row={row}
            remark={remark}
            setRemark={setRemark}
            auth={auth}
            user_current_office={user_current_office}
            getSingleuser={getSingleuser}
            handleBack={handleBack}
            StoreL1_id={StoreL1_id}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <BackButtonWrapper>
        <BackButton onClick={handleBack}>
          <ArrowLeftIcon />
          <span style={{ marginLeft: '8px' }}>Back to Inbox</span>
        </BackButton>
      </BackButtonWrapper>

      <ReturnReqFlow row={row} />

      <ActionContainer>
        {!showActionOptions ? (
          <ButtonsWrapper>
            {/* Don't show Take Action button if the request was rejected or closed,
                or if the request is not meant for this user */}
            {!isRequestClosedOrRejected && isRequestForUser() && (
              <Button onClick={handleTakeAction}>Take Action</Button>
            )}
          </ButtonsWrapper>
        ) : (
          <>
            {/* Show Assets to be Returned table only after clicking Take Action */}
            <SelectedAssetsContainer>
              <SelectedAssetsTitleContainer>
                <SelectedAssetsTitle>
                  Assets to be Returned
                  {hasModifiedAssets && <span style={{ fontSize: '0.8rem', color: '#4caf50', marginLeft: '10px' }}>(Modified)</span>}
                </SelectedAssetsTitle>
                <div>
                  {/* Show edit button when request is sent back for modification */}
                  {isModificationRequest && (
                    <Tooltip title="Modify Asset Selection">
                      <IconButton onClick={handleModifySelection} style={{ marginRight: '8px' }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </SelectedAssetsTitleContainer>

              {loading ? (
                <div style={{ 
                  padding: '30px', 
                  textAlign: 'center', 
                  color: '#666',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px' 
                }}>
                  Loading asset details...
                </div>
              ) : (() => {
                // Check if we have assets to display
                const latestRequest = row?.assets_requested?.[row.assets_requested.length - 1];
                const assetsToShow = hasModifiedAssets ? selectedAssets : (latestRequest?.data || []);
                return assetsToShow.length === 0;
              })() ? (
                <div style={{ 
                  padding: '30px', 
                  textAlign: 'center', 
                  color: '#666',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px' 
                }}>
                  No asset details available
                </div>
              ) : (
                <StyledTableContainer component={Paper}>
                  <Table aria-label="assets to be returned table">
                    <StyledTableHead>
                      <TableRow>
                        <TableCell width="60px">Sl.No</TableCell>
                        <TableCell width="120px">OEM Tag</TableCell>
                        <TableCell width="120px">Asset Name</TableCell>
                        <TableCell width="300px">Specifications</TableCell>
                        <TableCell width="100px">Purchase Date</TableCell>
                        <TableCell width="100px">Warranty Date</TableCell>
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {/* Show individual assets from the latest request */}
                      {(() => {
                        // Get assets from the latest request
                        const latestRequest = row?.assets_requested?.[row.assets_requested.length - 1];
                        const assetsToShow = hasModifiedAssets ? selectedAssets : 
                          (latestRequest?.data || []);
                        
                        return assetsToShow.map((assetObj, index) => {
                          // Handle both modified assets (processed format) and original assets (raw format)
                          const asset = hasModifiedAssets ? assetObj : {
                            id: assetObj.id,
                            oem_tag: assetObj.oem_tag,
                            name: assetObj.assetName,
                            specification: assetObj.spec,
                            purchase_date: assetObj.pur_date,
                            warrantyexp_date: assetObj.wrty_date
                          };
                          
                          return (
                            <TableRow key={index}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{asset.oem_tag || '-'}</TableCell>
                              <TableCell>{asset.name || asset.assetName || '-'}</TableCell>
                              <TableCell>
                                {asset.specification || asset.spec ? (
                                  <div style={{ 
                                    fontSize: '12px',
                                    width: '100%',
                                    minWidth: '280px',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px 15px',
                                    alignItems: 'flex-start'
                                  }}>
                                    {Object.entries(asset.specification || asset.spec || {})
                                      .filter(([key, value]) => value && value !== '')
                                      .map(([key, value], i) => (
                                        <span key={i} style={{ 
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          whiteSpace: 'nowrap',
                                          minWidth: 'fit-content'
                                        }}>
                                          <strong style={{ marginRight: '4px' }}>{key}:</strong> 
                                          <span>{value}</span>
                                        </span>
                                      ))}
                                  </div>
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                {asset.purchase_date || asset.pur_date ? 
                                  new Date(asset.purchase_date || asset.pur_date).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell>
                                {asset.warrantyexp_date || asset.wrty_date ? 
                                  new Date(asset.warrantyexp_date || asset.wrty_date).toLocaleDateString() : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              )}
            </SelectedAssetsContainer>

            {/* Render role-specific form */}
            {renderRoleSpecificForm()}
          </>
        )}
      </ActionContainer>

      {/* Modal for Modifying Asset Selection */}
      <Modal
        open={openModifyModal}
        onClose={handleCloseModifyModal}
        aria-labelledby="modify-asset-selection-modal"
      >
        <Box sx={ModifySelectionModalStyle}>
          <ModalTitle>Modify Asset Selection</ModalTitle>
          
          <UnivMyAssetComp 
            columnFilter={["OEM/ORG Tag", "OEM/Brand", "Model", "Store", "Detail"]}
            checkbox={true}
            selectedOptions={selectedAssets}
            setSelectedOptions={setSelectedAssets}
            selectAll={true}
            filterProps={{ 
              alloc_user: [auth?.id], 
              alloc_ofc: [row?.req_data[0]?.sent_by?.office],
              cur_store: [row?.req_store],
              indi_issue: row.req_type ===  'Ind_Asset_Return_Bo' ? 'true' : row.req_type === 'Asset_Return_Bo' ? 'false' : "Not Applicable" ,
            }}
          />
{/* 'Ind_Asset_Return_Bo' : "Asset_Return_Bo" */}
          <ModalFooter>
            <Button onClick={handleCloseModifyModal}>Cancel</Button>
            <ActionButton onClick={handleConfirmModification}
              disabled={selectedAssets.length === 0}>
              Confirm Selection
            </ActionButton>
          </ModalFooter>
        </Box>
      </Modal>
    </Container>
  );
};

export default UserAsstRtrnReqDetails;